import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, NavigationEnd, Router} from '@angular/router';
import {combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, filter, map, shareReplay, startWith} from 'rxjs/operators';
import {BRANDS, BrandId, DEFAULT_BRAND_ID, isBrandId} from '../../luxengine-brand-kit/brands';
import {faviconHTML} from '../../luxengine-brand-kit/brandbar';
import {UserTypeEnum} from '../enums';
import {AuthenticationService} from './authentication.service';

/**
 * Where angular.json serves the vendored kit's assets/ folder from. Pass this
 * as `assetBase` to the kit's helpers, and use brandAsset() for kit paths.
 */
export const BRAND_ASSET_BASE = 'assets/brand';

export function brandAsset(kitPath: string): string {
    return `${BRAND_ASSET_BASE}/${kitPath.replace(/^assets\//, '')}`;
}

/** LUXengine's primary lockup for a white/pale surface (raster preferred, per the kit). */
export const LUXENGINE_LOGO_ON_LIGHT = brandAsset(
    BRANDS.luxengine.logos.primary.onLightRaster ?? BRANDS.luxengine.logos.primary.onLight
);

/**
 * Resolves which brand the app is rendering and applies it to the document:
 * data-brand on <html> (which drives the kit's tokens.css and the --brand-*
 * roles in styles.scss) and the brand's favicon set.
 *
 * Signed in, the member's userType decides. Before sign-in, the deepest route's
 * `brand` data does (e.g. /login is LUXengine, /login/bwrm is BWRM), so the
 * pre-login pages paint the same brand as the logo they show.
 */
@Injectable({
    providedIn: 'root'
})
export class BrandService {
    readonly brand$: Observable<BrandId>;
    private originalIcons: Element[];

    constructor(private authService: AuthenticationService,
                private router: Router,
                @Inject(DOCUMENT) private document: Document) {
        const routeBrand$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            startWith(null),
            map(() => this.routeBrand(this.router.routerState.snapshot.root))
        );

        this.brand$ = combineLatest([this.authService.memberData$, routeBrand$]).pipe(
            map(([member, routeBrand]): BrandId => {
                if (member) {
                    return member.userType === UserTypeEnum.LUX_ENGINE ? 'luxengine' : 'bwrm';
                }
                return routeBrand ?? DEFAULT_BRAND_ID;
            }),
            distinctUntilChanged(),
            shareReplay({bufferSize: 1, refCount: true})
        );
    }

    apply(brand: BrandId): void {
        this.document.documentElement.setAttribute('data-brand', brand);
        this.applyFavicon(brand);
    }

    private routeBrand(route: ActivatedRouteSnapshot): BrandId | null {
        let brand: BrandId | null = null;
        for (let r: ActivatedRouteSnapshot | null = route; r; r = r.firstChild) {
            const value = r.data?.['brand'];
            if (isBrandId(value)) {
                brand = value;
            }
        }
        return brand;
    }

    /**
     * Swaps index.html's icon links for the kit's set (faviconHTML) when the
     * brand has one, and puts the originals back when it doesn't. BWRM has no
     * set in the kit yet, so it keeps index.html's.
     */
    private applyFavicon(brand: BrandId): void {
        const head = this.document.head;
        const selector = 'link[rel~="icon"], link[rel="apple-touch-icon"]';
        if (!this.originalIcons) {
            this.originalIcons = Array.from(head.querySelectorAll(selector));
        }
        head.querySelectorAll(selector).forEach(link => link.remove());

        const html = faviconHTML(brand, {assetBase: BRAND_ASSET_BASE});
        if (html) {
            head.insertAdjacentHTML('beforeend', html);
        } else {
            this.originalIcons.forEach(link => head.appendChild(link));
        }
    }
}
